import { useEffect, useState } from 'react';
import { apri, chiudi, idDallAncora } from '../../lib/ancora';
import type { FiltroColonna } from '../../lib/tipi';
import { usePuo, useSessione } from '../sessione/ContestoSessione';
import { DataGrid } from '../../components/grid/DataGrid';
import { SchedaRichiesta } from './SchedaRichiesta';
import { NuovaRichiestaDaElenco } from './NuovaRichiestaDaElenco';

const INTESTAZIONI: Record<string, string> = {
  DataRichiesta: 'Chiesta il',
  DataFabbisogno: 'Serve il',
  GiorniAlFabbisogno: 'Giorni',
  DaCodificare: 'Da codificare',
  ValoreStimato: 'Valore stimato',
};

type VistaRichieste = 'attesa' | 'mie' | 'tutte';

const VISTE: { chiave: VistaRichieste; etichetta: string }[] = [
  { chiave: 'attesa', etichetta: 'In attesa' },
  { chiave: 'mie', etichetta: 'Le mie' },
  { chiave: 'tutte', etichetta: 'Tutte' },
];

function filtriDellaVista(vista: VistaRichieste, mioNome: string | null): FiltroColonna[] | undefined {
  switch (vista) {
    case 'attesa':
      return [
        { colonna: 'Stato', operatore: 'EUnoDi', valori: ['Inviata'] },
        { colonna: 'Evasione', operatore: 'NonEUnoDi', valori: ['Completa'] },
      ];
    case 'mie':
      return [
        { colonna: 'Richiedente', operatore: 'EUnoDi', valori: [mioNome] },
        { colonna: 'Stato', operatore: 'NonEUnoDi', valori: ['Annullata'] },
      ];
    default:
      // Quelli del backend: tutto tranne le annullate.
      return undefined;
  }
}

export function ElencoRichieste() {
  const sessione = useSessione();
  const puoFare = usePuo();
  /*  Si parte da dove si lavora: chi compra dalle richieste in attesa, chi le scrive
      dalle sue.  */
  const [vista, setVista] = useState<VistaRichieste>(() =>
    puoFare('EmettiOrdini') ? 'attesa' : sessione.nomeCompleto !== null ? 'mie' : 'tutte',
  );
  const [aperta, setAperta] = useState<string | null>(() => idDallAncora('richieste'));

  /*  Come per i preventivi: `#richieste/<id>` apre quella richiesta. Ci arriva chi ne ha
      appena aperta una dalla commessa (16/10/2026), e chi ha un collegamento.  */
  useEffect(() => {
    /*  Solo quando l'ancora porta un id: App, cambiando schermata, riscrive l'ancora
        senza id subito dopo, e azzerare qui chiuderebbe la richiesta appena aperta.  */
    const rileggi = () => {
      const id = idDallAncora('richieste');
      if (id !== null) setAperta(id);
    };
    window.addEventListener('hashchange', rileggi);
    return () => window.removeEventListener('hashchange', rileggi);
  }, []);
  const [versione, setVersione] = useState(0);
  const [creando, setCreando] = useState(false);

  return (
    <section className="pagina con-scheda">
      <div className="colonna-principale">
        <header className="testata-pagina">
          <div>
            <h1>Richieste d’acquisto</h1>
            <p>
              Quello che i cantieri hanno chiesto. In cima le scadenze più vicine, perché è
              così che lavora chi compra. Apri una riga per vedere le sue righe, i residui e
              per inviarla.
            </p>
          </div>
          {puoFare('CreaRichieste') && (
            <button type="button" className="primario" onClick={() => setCreando(true)}>
              Nuova richiesta
            </button>
          )}
        </header>

        {/*  LE VISTE PRONTE, 16/10/2026. «L'ufficio acquisti deve vedere solo quelle
             inviate»: una bozza e' di chi la sta scrivendo, e finche' non la manda non
             e' una richiesta. «In attesa» sono le inviate non ancora ordinate del tutto —
             la RDA dimenticata si vede li', senza cercarla per numero. «Le mie» e' di chi
             le scrive, bozze comprese. Sono filtri come gli altri: si vedono e si tolgono.  */}
        <div className="viste-pronte" role="group" aria-label="Viste">
          {VISTE.filter((v) => v.chiave !== 'mie' || sessione.nomeCompleto !== null).map((v) => (
            <button
              key={v.chiave}
              type="button"
              className={vista === v.chiave ? 'on' : ''}
              aria-pressed={vista === v.chiave}
              onClick={() => setVista(v.chiave)}
            >
              {v.etichetta}
            </button>
          ))}
        </div>

        <DataGrid
          key={vista}
          filtriIniziali={filtriDellaVista(vista, sessione.nomeCompleto)}
          nomeGriglia="rda"
          versione={versione}
          colonneVisibili={[
            'Numero',
            'Sede',
            'Stato',
            'Evasione',
            'InRitardo',
            'DataFabbisogno',
            'GiorniAlFabbisogno',
            'Commessa',
            'Cliente',
            'Descrizione',
            'Richiedente',
            'Righe',
            'DaCodificare',
            'ValoreStimato',
          ]}
          intestazioni={INTESTAZIONI}
          idSelezionato={aperta}
          onRigaSelezionata={(riga) => {
            const id = riga['Id'];
            if (typeof id === 'string') {
              setCreando(false);
              setAperta(id);
              apri('richieste', id);
            }
          }}
        />
      </div>

      {creando && (
        <NuovaRichiestaDaElenco
          onAnnulla={() => setCreando(false)}
          onCreata={(id) => {
            setCreando(false);
            setAperta(id);
            apri('richieste', id);
            setVersione((v) => v + 1);
          }}
        />
      )}

      {!creando && aperta && (
        <SchedaRichiesta
          richiestaId={aperta}
          onChiudi={() => {
            setAperta(null);
            chiudi('richieste');
          }}
          // Inviare o annullare cambia lo stato, e l'elenco lo mostra in colonna:
          // se non si ricarica, la griglia dice ancora «Bozza» accanto alla scheda
          // che dice «Inviata», e a quel punto non si sa a quale credere.
          onCambiata={() => setVersione((v) => v + 1)}
        />
      )}
    </section>
  );
}
